import React, { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Loader from "./components/Loader";
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme'; // Import MUI theme
import { AuthProvider } from "./context/authContext";
import PrivateRoute from "./PrivateRoute"; // Import PrivateRoute
import "./index.css"; // Import global CSS file

// Lazy load pages
const HomePage = lazy(() => import("./pages/Homepage"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const News = lazy(() => import("./pages/News"));
const Rooms = lazy(() => import("./pages/Rooms"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const BookingPage = lazy(() => import("./pages/Booking"));
const SignInPage = lazy(() => import("./pages/SignIn"));
const SignUpPage = lazy(() => import("./pages/SignUpPage"));
const DashboardPage = lazy(() => import("./pages/Dashboard"));
const DesignShowcase = lazy(() => import("./components/DesignShowcase"));

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to the top on route change
  }, [pathname]);

  return null;
};

const App = () => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Show the loader when the location changes
    setLoading(true);
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 3000); // Simulate loading time (adjust as needed)

    return () => clearTimeout(timeout); // Cleanup timeout on unmount
  }, [location]);

  return (
    <>
      <ScrollToTop /> {/* Scroll to top when route changes */}
      {loading ? (
        <Loader /> // Show loader while loading
      ) : (
        <Suspense fallback={<Loader />}> {/* Display loader until the component is loaded */}
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/news" element={<News />} />
            <Route path="/rooms" element={<Rooms />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/booking" element={<BookingPage />} />

            {/* Protect Dashboard route with PrivateRoute */}
            <Route
              path="/dashboard"
              element={<PrivateRoute element={<DashboardPage />} />}
            />

            {/* Temporary route for Design System Showcase */}
            <Route path="/design-system" element={<DesignShowcase />} />

            <Route path="/signin" element={<SignInPage />} />
            <Route path="/signup" element={<SignUpPage />} />
          </Routes>
        </Suspense>
      )}
    </>
  );
};



const AppWrapper = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <Router>
      <AuthProvider>
        <App />
      </AuthProvider>
    </Router>
  </ThemeProvider>
);

export default AppWrapper;
